import type { ApiResponse } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import type {
  CreateChatRequest,
  CreateChatResponse,
  GetChatResponse,
  ListChatResponse,
  ListMessagesResponse,
} from "../../../schemas/codegen";
import { api } from "../api/api";

const CHATS_KEY = "CHATS";
const PROFILE_KEY = "PROFILE";

export const getProfileQueryOptions = () => ({
  queryKey: [PROFILE_KEY],
  queryFn: () => api.getProfile(),
});

export const listChatsQueryOptions = () => ({
  queryKey: [CHATS_KEY],
  queryFn: () => api.listChats(),
  select: (response: ApiResponse<ListChatResponse>) => response.data,
});

export const getChatQueryOptions = (chatId: string) => ({
  queryKey: [CHATS_KEY, chatId],
  queryFn: () => api.getChat(chatId),
  select: (response: ApiResponse<GetChatResponse>) => response.data,
  enabled: !!chatId,
});

export const listMessagesQueryOptions = (chatId: string) => ({
  queryKey: [CHATS_KEY, chatId, "messages"],
  queryFn: () => api.listMessages(chatId),
  select: (response: ApiResponse<ListMessagesResponse>) => response.data,
  enabled: !!chatId,
});

export function useCreateChat() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: (data: CreateChatRequest) => api.createChat(data),
    onSuccess: async (response: ApiResponse<CreateChatResponse>) => {
      // Prefetch the chat data before navigation
      await queryClient.prefetchQuery(
        getChatQueryOptions(response.data.chat.id),
      );

      // Navigate to the new chat
      router.navigate({
        to: "/codegen/$chatId",
        params: { chatId: response.data.chat.id },
      });

      // Invalidate the chat list in the background
      queryClient.invalidateQueries({ queryKey: [CHATS_KEY] });
    },
  });
}
